/**
 * Work-item embed — Phase 4c inline atomic embed (wiki feature plan §4c).
 *
 * Inline chip rendered as `<span data-block-type="work-item-embed">`.
 * Click opens the existing `<CardDetailModal>` in-place via a per-doc
 * modal owner; the NodeView mounts `useCardEmbed(cardId)` to resolve
 * the chip-render subset of the referenced card (kind / code / title /
 * status / assignee) and subscribes to `card.updated` WS frames so the
 * chip ticks live.
 *
 * Schema shape — inline atomic, mirrors the status lozenge contract:
 *   - `group: 'inline'` + `inline: true` + `atom: true`
 *   - `selectable: true`, `draggable: false`, `marks: ''`
 *
 * Attributes:
 *   - `projectId: string | null` — the card's owning project. Useful
 *     for the modal open path (CardDetailModal needs project context
 *     for status catalog + members) without an extra fetch.
 *   - `cardId: string | null` — the actual reference. Resolved through
 *     `useCardEmbed`. Both attrs are independently `null` while the
 *     picker is still open so the chip can mount in a placeholder
 *     state.
 *   - `kind: DeliverableKind | null` — a LOOKUP HINT, never a
 *     contract. Cards live in four tables, so a bare id costs the BE an
 *     extra statement to work out which one; the picker knows the kind
 *     at insert time, so newly-authored chips carry it and resolve in a
 *     single statement. **Nullable on purpose** — a chip without it (an
 *     older doc, a paste, a hand-written node) renders identically via
 *     the BE's discriminator. Never make it required, and never treat a
 *     missing/garbage value as an error: `useCardEmbed` simply omits
 *     `?kind=` and the BE resolves it. The chip still reads the
 *     authoritative `kind` off the RESPONSE for rendering.
 *
 * Round-trip:
 *   - parseHTML matches `span[data-block-type="work-item-embed"]`.
 *   - renderHTML emits `data-project-id` + `data-card-id` + `data-kind`
 *     (each omitted when `null` — keeps the on-disk JSON clean for
 *     never-resolved embeds, should those ever appear via paste /
 *     migration).
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

import type { DeliverableKind } from '@/api/types-bridge'

/**
 * Strict UUID matcher. Defends the NodeView against malformed attrs
 * arriving via paste / serialization corruption.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

/**
 * The four deliverable kinds, mirrored here rather than imported as a
 * value so the extension stays free of runtime deps on the API layer.
 * Anything outside this set parses to `null` — a garbage hint must
 * degrade to "no hint", never to a broken chip.
 */
const DELIVERABLE_KINDS: readonly DeliverableKind[] = ['task', 'ticket', 'event', 'announcement']

function isDeliverableKind(value: unknown): value is DeliverableKind {
  return typeof value === 'string' && (DELIVERABLE_KINDS as readonly string[]).includes(value)
}

/**
 * Human card reference — project code (2–5 chars, letter-initial, per the
 * BE's `projects.code` CHECK) plus the per-project sequence number. Kept
 * in step with the BE validator's `attrs.shorthand` contract, which
 * REJECTS an off-shape value rather than nulling it, so anything this
 * accepts must be something the BE accepts too.
 */
const SHORTHAND_RE = /^[a-z][a-z0-9]{1,4}-\d{1,9}$/i

function isCardShorthand(value: unknown): value is string {
  return typeof value === 'string' && SHORTHAND_RE.test(value)
}

export interface BuildWorkItemEmbedExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildWorkItemEmbedExtension({ view }: BuildWorkItemEmbedExtensionOptions) {
  return Node.create({
    name: 'workItemEmbed',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: '',

    addAttributes() {
      return {
        projectId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-project-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { projectId: string | null }) => {
            return attrs.projectId ? { 'data-project-id': attrs.projectId } : {}
          },
        },
        cardId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-card-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { cardId: string | null }) => {
            return attrs.cardId ? { 'data-card-id': attrs.cardId } : {}
          },
        },
        kind: {
          default: null as DeliverableKind | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-kind')
            return isDeliverableKind(raw) ? raw : null
          },
          renderHTML: (attrs: { kind: DeliverableKind | null }) => {
            return attrs.kind ? { 'data-kind': attrs.kind } : {}
          },
        },
        shorthand: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-shorthand')
            return isCardShorthand(raw) ? raw.toUpperCase() : null
          },
          renderHTML: (attrs: { shorthand: string | null }) => {
            return attrs.shorthand ? { 'data-shorthand': attrs.shorthand } : {}
          },
        },
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-block-type="work-item-embed"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, 'data-block-type': 'work-item-embed' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}

export { isUuid }
